// 汇率表 - 用于预算系统的货币换算
// 基准货币为 RMB (人民币)

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  rate: number; // 相对于RMB的汇率 (1 RMB = rate * 外币)
}

// 货币信息和汇率数据
export const CURRENCY_DATA: Record<string, CurrencyInfo> = {
  'RMB': {
    code: 'RMB',
    symbol: '¥',
    name: '人民币',
    rate: 1.0000 // 基准货币
  },
  'HK$': {
    code: 'HK$',
    symbol: 'HK$',
    name: '港币',
    rate: 1.0886 // 1 RMB = 0.9 HKD
  },
  'US$': {
    code: 'US$',
    symbol: 'US$',
    name: '美元',
    rate: 0.1387 // 1 RMB = 0.14 USD
  },
  'EUR': {
    code: 'EUR',
    symbol: '€',
    name: '欧元',
    rate: 0.1215 // 1 RMB = 0.13 EUR
  },
  'JPY': {
    code: 'JPY',
    symbol: '¥',
    name: '日元',
    rate: 20.8784 // 1 RMB = 20.5 JPY
  },
  'GBP': {
    code: 'GBP',
    symbol: '£',
    name: '英镑',
    rate: 0.1025 // 1 RMB = 0.11 GBP
  },
  'CAD': {
    code: 'CAD',
    symbol: 'CAD$',
    name: '加元',
    rate: 0.1924 // 1 RMB = 0.19 CAD
  },
  'AUD': {
    code: 'AUD',
    symbol: 'AUD$',
    name: '澳元',
    rate: 0.2157 // 1 RMB = 0.21 AUD
  }
};

// 获取货币符号
export const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_DATA[currencyCode]?.symbol || currencyCode;
};

// 获取货币名称
export const getCurrencyName = (currencyCode: string): string => {
  return CURRENCY_DATA[currencyCode]?.name || currencyCode;
};

// 获取汇率 (相对于RMB)
export const getExchangeRate = (currencyCode: string): number => {
  return CURRENCY_DATA[currencyCode]?.rate || 1.0;
};

// 货币换算函数 - 从一种货币转换为另一种货币
export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string
): number => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  const fromRate = getExchangeRate(fromCurrency);
  const toRate = getExchangeRate(toCurrency);
  
  // 先转换为RMB，再转换为目标货币
  const rmbAmount = amount / fromRate;
  const targetAmount = rmbAmount * toRate;
  
  return targetAmount;
};

// 将金额转换为RMB (基准货币)
export const convertToBaseCurrency = (amount: number, fromCurrency: string): number => {
  return convertCurrency(amount, fromCurrency, 'RMB');
};

// 从RMB转换为指定货币
export const convertFromBaseCurrency = (amount: number, toCurrency: string): number => {
  return convertCurrency(amount, 'RMB', toCurrency);
};

// 格式化金额显示
export const formatCurrencyAmount = (
  amount: number,
  currencyCode: string,
  showSymbol: boolean = true
): string => {
  const symbol = showSymbol ? getCurrencySymbol(currencyCode) : '';
  const formattedAmount = amount.toLocaleString('en-HK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true
  });
  
  return showSymbol ? `${symbol} ${formattedAmount}` : formattedAmount;
};

// 计算多个不同货币金额的总和 (转换为指定目标货币)
export const sumAmountsInTargetCurrency = (
  amounts: Array<{ amount: number; currency: string }>,
  targetCurrency: string
): number => {
  return amounts.reduce((total, item) => {
    const convertedAmount = convertCurrency(item.amount, item.currency, targetCurrency);
    return total + convertedAmount;
  }, 0);
};

// 获取支持的货币列表
export const getSupportedCurrencies = (): CurrencyInfo[] => {
  return Object.values(CURRENCY_DATA);
};

// 验证货币代码是否支持
export const isSupportedCurrency = (currencyCode: string): boolean => {
  return currencyCode in CURRENCY_DATA;
};

// 更新汇率 (用于将来的动态汇率更新)
export const updateExchangeRate = (currencyCode: string, newRate: number): boolean => {
  if (currencyCode === 'RMB') {
    console.warn('不能修改基准货币RMB的汇率');
    return false;
  }
  
  if (currencyCode in CURRENCY_DATA) {
    CURRENCY_DATA[currencyCode].rate = newRate;
    return true;
  }
  
  return false;
};

// 批量更新汇率
export const updateExchangeRates = (rates: Record<string, number>): void => {
  Object.entries(rates).forEach(([currencyCode, rate]) => {
    updateExchangeRate(currencyCode, rate);
  });
}; 